import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  AlertTriangle,
  CheckCircle2,
  Download,
  FileWarning,
  Loader2,
  Upload,
  XCircle,
} from 'lucide-react';
import { rowsOf, useApi } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { LoadingState } from '@/components/ui/states';
import { Table, TD, TH, THead, TR } from '@/components/ui/table';
import {
  apiError,
  cancelJob,
  canCancelJob,
  canEditMapping,
  commitJob,
  downloadBlob,
  fetchDatasets,
  fetchErrorReport,
  fetchJob,
  fetchTemplate,
  formatBytes,
  hasAttemptedCommit,
  localize,
  uploadFile,
  validateJob,
  type ColumnMapping,
  type DatasetsResponse,
  type ImportDatasetId,
  type ImportDatasetSpec,
  type ImportJobDetail,
  type ImportOptions,
  type JobTotals,
  type RowIssue,
  type UploadResult,
} from './lib';

/**
 * A guess, named as one.
 *
 * ADR-0007 §2.4 says a 20,000-row commit is ~100 round trips of 200 rows each
 * "measured in seconds", not minutes — that is the ADR's own justification for
 * running it inside the request rather than a queue. 120s for commit and 60s
 * for a read-only validate are multiples of that claim, chosen because the
 * default HTTP client timeout (20s, `lib/api-client/http.ts`) is comfortably
 * too little for a worst-case file and I have not measured the real number.
 *
 * What would invalidate this: the actual per-batch cost on production
 * hardware and a real 20,000-row file (I have neither — no live backend to
 * time one against); a slow step *inside* `dryRun` that scales worse than
 * linearly with row count, e.g. `prepare()`'s per-dataset reads of the
 * tenant's *entire* existing table before matching a batch against it, which
 * could make a large *tenant* slow to validate even for a small *file*; and
 * `IMPORT_BATCH_ROWS` (200) or the 8 MB / 20,000-row upload ceilings changing
 * on the backend without this changing to match. If the reconcile-after-
 * timeout path (`reconcileAfterTimeout`, below) is firing often in practice,
 * that is the signal these are too short — raise them before trusting the
 * poll loop to paper over it on every large import.
 */
const COMMIT_TIMEOUT_MS = 120_000;
const VALIDATE_TIMEOUT_MS = 60_000;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Resume an existing job from the history list. `null` starts a new one. */
  jobId: string | null;
  /** Pre-select a dataset when starting fresh — e.g. a cross-link from Catalog. */
  initialDataset?: ImportDatasetId;
  /** Called after upload, validate, commit or cancel — the caller reloads its list. */
  onChanged: () => void;
}

/** What the mapping/result view needs, whichever request produced it. */
interface RunResult {
  status: ImportJobDetail['status'];
  totals: JobTotals;
  errorCount: number;
  warningCount: number;
  issues: RowIssue[];
  truncated: boolean;
}

export default function ImportWizard({ open, onClose, jobId, initialDataset, onChanged }: Props) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const { can, permissions } = usePermission();
  // Upload, validate and cancel all write nothing beyond the job row, and all
  // three are gated on this one key server-side — see `ImportsController`.
  const canPrepare = can('imports.create');

  const [datasetsInfo, setDatasetsInfo] = useState<DatasetsResponse | null>(null);
  const [loadingShell, setLoadingShell] = useState(true);
  const [shellError, setShellError] = useState<string | null>(null);

  const [dataset, setDataset] = useState<ImportDatasetId | null>(initialDataset ?? null);
  const [job, setJob] = useState<ImportJobDetail | null>(null);
  const [sample, setSample] = useState<UploadResult['sample']>([]);
  const [mapping, setMapping] = useState<ColumnMapping>({});
  const [options, setOptions] = useState<ImportOptions>({});
  const [run, setRun] = useState<RunResult | null>(null);

  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [validating, setValidating] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [reconciling, setReconciling] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  const spec: ImportDatasetSpec | null = useMemo(
    () => datasetsInfo?.datasets.find((d) => d.dataset === dataset) ?? null,
    [datasetsInfo, dataset],
  );

  // Memoized so the `[] ` fallback is a stable reference between renders —
  // otherwise `unmappedColumns` below would recompute on every render even
  // when nothing about the job's columns actually changed.
  const columns: string[] = useMemo(() => job?.columns ?? [], [job?.columns]);

  // `isActive: true` on warehouses is not cosmetic: `import-writers.ts`'s
  // `prepare()` resolves OPENING_STOCK's warehouse list from
  // `db.warehouse.findMany({ where: { tenantId, isActive: true } })`, so an
  // inactive warehouse offered here would validate as `unknown_warehouse`.
  // Units carry no such filter server-side for INVENTORY_ITEMS — an inactive
  // unit still resolves — so this is a UX default there, not a correctness
  // fix; confirmed against `Tarabeeza-Backend/src/modules/inventory/{units,
  // warehouses}.service.ts` and the `Warehouse`/`Unit` Prisma models (`id`,
  // `code`, `name` all non-nullable `String`).
  const warehouses = useApi<unknown>(
    open && dataset === 'OPENING_STOCK' && can('inventory.view') ? '/inventory/warehouses' : null,
    { limit: 200, isActive: true },
  );
  const warehouseRows = rowsOf<{ id: string; code: string; name: string }>(warehouses.data).rows;
  const units = useApi<unknown>(
    open && dataset === 'INVENTORY_ITEMS' && can('inventory.view') ? '/inventory/units' : null,
    { limit: 200, isActive: true },
  );
  const unitRows = rowsOf<{ id: string; code: string; name: string }>(units.data).rows;

  const resetLocal = useCallback(() => {
    setDataset(initialDataset ?? null);
    setJob(null);
    setSample([]);
    setMapping({});
    setOptions({});
    setRun(null);
    setUploadError(null);
    if (fileInput.current) fileInput.current.value = '';
  }, [initialDataset]);

  // Load the shell (datasets + specs, and the job if resuming) every time the
  // dialog opens. Not on every render: the mapping the operator is building
  // must not be wiped out by an unrelated re-render of the parent list.
  useEffect(() => {
    if (!open) return;
    let live = true;
    setLoadingShell(true);
    setShellError(null);
    resetLocal();

    Promise.all([fetchDatasets(), jobId ? fetchJob(jobId) : Promise.resolve(null)])
      .then(([datasets, loadedJob]) => {
        if (!live) return;
        setDatasetsInfo(datasets);
        if (loadedJob) {
          setJob(loadedJob);
          setDataset(loadedJob.dataset);
          setMapping(loadedJob.mapping ?? {});
          setOptions(loadedJob.options ?? {});
          if (loadedJob.totals) {
            setRun({
              status: loadedJob.status,
              totals: loadedJob.totals,
              errorCount: loadedJob.errorCount,
              warningCount: loadedJob.warningCount,
              issues: loadedJob.issues,
              // `errorCount`/`warningCount` are the true totals `validate()`
              // found; `issues` is capped to the first 100 inline. More of
              // either than fit in the list means there is more to see than
              // this screen is showing — say so, and point at the download.
              truncated: loadedJob.errorCount + loadedJob.warningCount > loadedJob.issues.length,
            });
          }
        }
      })
      .catch((e) => live && setShellError(apiError(e, t('imports.errors.loadFailed'))))
      .finally(() => live && setLoadingShell(false));

    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, jobId]);

  const close = () => {
    if (uploading || validating || committing) return;
    onClose();
  };

  // ---------------------------------------------------------------------
  // Step 1 — dataset (new imports only)
  // ---------------------------------------------------------------------
  const pickDataset = (id: ImportDatasetId) => setDataset(id);

  const downloadTemplate = async (id: ImportDatasetId) => {
    try {
      const blob = await fetchTemplate(id);
      downloadBlob(blob, `tarabeeza-${id.toLowerCase()}-template.xlsx`);
    } catch (e) {
      toast.error(apiError(e, t('imports.errors.templateFailed')));
    }
  };

  // ---------------------------------------------------------------------
  // Step 2 — upload
  // ---------------------------------------------------------------------
  const choose = async (file: File) => {
    if (!dataset) return;
    setUploadError(null);
    setUploading(true);
    try {
      const result = await uploadFile(dataset, file);
      setSample(result.sample);
      setMapping(result.suggestedMapping);
      const loaded = await fetchJob(result.id);
      setJob(loaded);
      onChanged();
    } catch (e) {
      setUploadError(apiError(e, t('imports.errors.uploadFailed')));
      if (fileInput.current) fileInput.current.value = '';
    } finally {
      setUploading(false);
    }
  };

  // ---------------------------------------------------------------------
  // Step 3 — mapping
  // ---------------------------------------------------------------------
  const mappingLocked = job ? !canEditMapping(job.status) : false;

  const setFieldForColumn = (column: string, field: string | null) => {
    setMapping((prev) => {
      const next = { ...prev };
      // One column per field: claiming it here releases whoever held it.
      if (field) {
        for (const c of Object.keys(next)) {
          if (c !== column && next[c] === field) next[c] = null;
        }
      }
      next[column] = field;
      return next;
    });
    // The mapping changed underneath whatever the last dry run said about it.
    setRun(null);
  };

  const unmappedColumns = useMemo(
    () => columns.filter((c) => !mapping[c]),
    [columns, mapping],
  );

  const mappingError: string | null = useMemo(() => {
    if (!spec) return null;
    const mappedFields = new Set(Object.values(mapping).filter((f): f is string => !!f));
    const missingRequired = spec.fields.filter((f) => f.required && !mappedFields.has(f.key));
    if (missingRequired.length) {
      return t('imports.wizard.mapping.missingRequired', {
        fields: missingRequired
          .map((f) => localize(f.label, f.labelAr, lang))
          .join(t('imports.wizard.mapping.listSeparator')),
      });
    }
    if (!spec.matchedOn.some((key) => mappedFields.has(key))) {
      const names = spec.matchedOn
        .map((key) => spec.fields.find((f) => f.key === key))
        .filter((f): f is NonNullable<typeof f> => !!f)
        .map((f) => localize(f.label, f.labelAr, lang))
        .join(` ${t('imports.wizard.mapping.or')} `);
      return t('imports.wizard.mapping.noKeyMapped', { names });
    }
    return null;
  }, [spec, mapping, t, lang]);

  const runValidate = async () => {
    if (!job || mappingError) return;
    setValidating(true);
    try {
      const data = await validateJob(job.id, mapping, options, VALIDATE_TIMEOUT_MS);
      setRun({
        status: data.status,
        totals: data.totals,
        errorCount: data.errorCount,
        warningCount: data.warningCount,
        issues: data.issues,
        // `data.truncated` is whether the FULL issue list passed 2,000 (the
        // storage cap) — a much larger threshold than the 100 shown inline
        // here, so it understates "there's more than what you're looking at".
        truncated: data.errorCount + data.warningCount > data.issues.length,
      });
      setJob((prev) => (prev ? { ...prev, status: data.status } : prev));
      onChanged();
    } catch (e) {
      toast.error(apiError(e, t('imports.errors.validateFailed')));
    } finally {
      setValidating(false);
    }
  };

  // ---------------------------------------------------------------------
  // Step 4 — commit
  // ---------------------------------------------------------------------
  /**
   * ADR-0007 §2.8 — landing an import needs `imports.commit` *and* the
   * dataset's own write key, two independent gates. A test written for the
   * "commit is blocked and says why" case caught this the first time round:
   * the old version only ever reported the dataset keys, so an actor missing
   * BOTH `imports.commit` and, say, `customers.create` was told only about
   * `customers.create` — true, but not the whole reason, and an actor who
   * fixed the dataset key would still be silently refused. Every missing key
   * is named now, `imports.commit` included.
   */
  const missingCommitPerms = [
    ...(can('imports.commit') ? [] : ['imports.commit']),
    ...(spec?.commitPermissions.filter((p) => !permissions.includes(p)) ?? []),
  ];
  const canCommitThis =
    !!job && datasetsInfo?.committable.includes(job.dataset) && missingCommitPerms.length === 0;

  /** A disabled Commit button with no stated reason is its own bug. */
  const commitBlockedReason: string | null = !job
    ? null
    : !canCommitThis
      ? missingCommitPerms.length
        ? t('imports.wizard.commit.needsPermissions', { perms: missingCommitPerms.join(', ') })
        // The server's own `committable` list excludes this dataset for a
        // reason our local mirror of the actor's permissions cannot name —
        // trust the server rather than staying silent about it.
        : t('imports.wizard.commit.needsCommitPermission')
      : job.status === 'UPLOADED'
        ? t('imports.wizard.commit.needsValidate')
        : job.status === 'INVALID'
          ? t('imports.wizard.commit.needsFix')
          : job.status === 'COMMITTING'
            ? t('imports.wizard.status.committingHint')
            : null;

  /** After a timeout the server may still be writing. Ask again rather than
   *  guessing — a job that finished COMMITTING is never reported as failed. */
  const reconcileAfterTimeout = useCallback(
    async (id: string) => {
      setReconciling(true);
      for (let attempt = 0; attempt < 10; attempt += 1) {
        await new Promise((resolve) => setTimeout(resolve, 3000));
        try {
          const latest = await fetchJob(id);
          if (latest.status !== 'COMMITTING') {
            setJob(latest);
            if (latest.totals) {
              setRun({
                status: latest.status,
                totals: latest.totals,
                errorCount: latest.errorCount,
                warningCount: latest.warningCount,
                issues: latest.issues,
                truncated: latest.errorCount + latest.warningCount > latest.issues.length,
              });
            }
            onChanged();
            setReconciling(false);
            return;
          }
        } catch {
          // Keep trying — a transient read failure here is not the answer.
        }
      }
      setReconciling(false);
    },
    [onChanged],
  );

  const runCommit = async () => {
    if (!job) return;
    setCommitting(true);
    try {
      const data = await commitJob(job.id, COMMIT_TIMEOUT_MS);
      setJob((prev) => (prev ? { ...prev, status: data.status, totals: data.totals } : prev));
      setRun((prev) => (prev ? { ...prev, status: data.status, totals: data.totals } : prev));
      onChanged();
    } catch (e) {
      const timedOut =
        (e as { code?: string })?.code === 'ECONNABORTED' ||
        /timeout/i.test((e as Error)?.message ?? '');
      if (timedOut) {
        toast.message(t('imports.wizard.commit.stillRunning'));
        void reconcileAfterTimeout(job.id);
      } else {
        toast.error(apiError(e, t('imports.errors.commitFailed')));
      }
    } finally {
      setCommitting(false);
    }
  };

  const runCancel = async () => {
    if (!job) return;
    setCancelling(true);
    try {
      await cancelJob(job.id);
      toast.success(t('imports.wizard.cancelled'));
      onChanged();
      onClose();
    } catch (e) {
      toast.error(apiError(e, t('imports.errors.cancelFailed')));
    } finally {
      setCancelling(false);
    }
  };

  const downloadReport = async () => {
    if (!job) return;
    try {
      const { fileName, blob } = await fetchErrorReport(job.id);
      downloadBlob(blob, fileName);
    } catch (e) {
      toast.error(apiError(e, t('imports.errors.reportFailed')));
    }
  };

  // ---------------------------------------------------------------------
  const title = spec ? localize(spec.label, spec.labelAr, lang) : t('imports.wizard.title');

  return (
    <Dialog
      open={open}
      onClose={close}
      title={title}
      description={job ? job.fileName : t('imports.wizard.subtitle')}
      className="w-full max-w-[1100px]"
      // Called, not rendered as `<Footer />`: these are closures over this
      // render's state, not their own component type. JSX would give each one
      // a fresh identity every render — which is a real bug here, not just
      // waste, because it would remount the mapping table and its inputs on
      // every keystroke and drop focus out from under whoever is typing.
      footer={Footer()}
    >
      {loadingShell ? (
        <LoadingState label={t('common.loading')} />
      ) : shellError ? (
        <p className="flex items-center gap-2 text-sm text-red-600 dark:text-red-400">
          <XCircle className="h-4 w-4" /> {shellError}
        </p>
      ) : !dataset ? (
        DatasetPicker()
      ) : !job ? (
        UploadStep()
      ) : (
        MappingAndResult()
      )}
    </Dialog>
  );

  // ---------------------------------------------------------------------
  // Sub-views (closures — they read the state above directly)
  // ---------------------------------------------------------------------

  function Footer() {
    if (!dataset || !job) return null;
    // `run` is the freshest word on issues — a validate just run in this
    // dialog, or the totals a resumed job already carried. `job.errorCount`
    // alone would still read zero right after a validate that found problems,
    // since that response updates `job.status` but not its counts.
    const issueCount = (run?.errorCount ?? job.errorCount) + (run?.warningCount ?? job.warningCount);
    return (
      <div className="flex w-full flex-wrap items-center justify-between gap-3">
        {/* A disabled Commit (or Check) with no stated reason is its own bug
            — Catalog's own importer names this exact rule. Both reasons are
            rendered as text, not only as a hover `title`: a permission gap
            in particular is not something a mouse-hover discovers on a
            screen a keyboard or touch user is looking at. */}
        <div className="flex flex-col gap-0.5 text-xs text-ink-muted">
          {!mappingLocked && mappingError && <span>{mappingError}</span>}
          {commitBlockedReason && <span>{commitBlockedReason}</span>}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {canCancelJob(job.status) && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => void runCancel()}
              disabled={cancelling || !canPrepare}
              title={canPrepare ? undefined : t('imports.wizard.needsCreatePermission')}
            >
              {cancelling && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('imports.wizard.cancel')}
            </Button>
          )}
          {issueCount > 0 && (
            <Button variant="outline" size="sm" onClick={() => void downloadReport()}>
              <Download className="h-4 w-4" />
              {t('imports.wizard.downloadReport')}
            </Button>
          )}
          {!mappingLocked && (
            <Button
              size="sm"
              onClick={() => void runValidate()}
              disabled={validating || !!mappingError || !canPrepare}
              title={mappingError ?? (canPrepare ? undefined : t('imports.wizard.needsCreatePermission'))}
            >
              {validating && <Loader2 className="h-4 w-4 animate-spin" />}
              {t('imports.wizard.checkFile')}
            </Button>
          )}
          {job.status !== 'COMMITTED' && job.status !== 'CANCELLED' && (
            <Button
              size="sm"
              onClick={() => void runCommit()}
              disabled={committing || reconciling || !!commitBlockedReason}
              title={commitBlockedReason ?? undefined}
            >
              {(committing || reconciling) && <Loader2 className="h-4 w-4 animate-spin" />}
              {/* `job.status` is already narrowed off `COMMITTED` by the
                  guard around this button, so a prior attempt that landed
                  fully can only mean `PARTIALLY_COMMITTED` or `FAILED` here. */}
              {hasAttemptedCommit(job.status)
                ? t('imports.wizard.commit.resume')
                : t('imports.wizard.commit.action')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  function DatasetPicker() {
    if (!datasetsInfo) return null;
    return (
      <div className="space-y-3">
        <p className="text-sm text-ink-muted">{t('imports.wizard.dataset.hint')}</p>
        <div className="grid gap-3 sm:grid-cols-2">
          {datasetsInfo.datasets.map((d) => {
            const committable = datasetsInfo.committable.includes(d.dataset);
            return (
              // A `<div>`, not a `<button>`: it holds a real, separately
              // focusable download button below, and a button cannot legally
              // contain interactive content — nesting one breaks both the
              // markup and the tab order.
              <div
                key={d.dataset}
                role="button"
                tabIndex={0}
                onClick={() => pickDataset(d.dataset)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    pickDataset(d.dataset);
                  }
                }}
                className="flex cursor-pointer flex-col items-start gap-2 rounded-lg border-2 border-[var(--border)] p-4 text-start hover:border-[var(--primary)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="flex w-full items-center justify-between gap-2">
                  <span className="font-medium">{localize(d.label, d.labelAr, lang)}</span>
                  <Badge tone={committable ? 'success' : 'neutral'}>
                    {committable
                      ? t('imports.wizard.dataset.canCommit')
                      : t('imports.wizard.dataset.prepareOnly')}
                  </Badge>
                </span>
                <span className="text-xs text-ink-muted">
                  {t('imports.wizard.dataset.matchedOn', {
                    fields: d.matchedOn
                      .map((key) => {
                        const f = d.fields.find((field) => field.key === key);
                        return f ? localize(f.label, f.labelAr, lang) : key;
                      })
                      .join(` ${t('imports.wizard.mapping.then')} `),
                  })}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    void downloadTemplate(d.dataset);
                  }}
                  className="mt-1 inline-flex items-center gap-1 text-xs text-accent underline"
                >
                  <Download className="h-3 w-3" />
                  {t('imports.wizard.dataset.template')}
                </button>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  function UploadStep() {
    return (
      <div className="space-y-4">
        {spec && (
          <p className="text-sm text-ink-muted">
            {t('imports.wizard.upload.hint', {
              max: formatBytes(datasetsInfo?.limits.maxUploadBytes ?? 8_388_608),
            })}
          </p>
        )}
        <input
          ref={fileInput}
          type="file"
          accept=".xlsx,.csv"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void choose(file);
          }}
        />
        <button
          type="button"
          onClick={() => fileInput.current?.click()}
          disabled={uploading || !canPrepare}
          title={canPrepare ? undefined : t('imports.wizard.needsCreatePermission')}
          className="flex w-full flex-col items-center gap-2 rounded-lg border-2 border-dashed border-[var(--border)] p-10 hover:border-[var(--primary)] disabled:cursor-not-allowed disabled:opacity-60"
        >
          {uploading ? (
            <Loader2 className="h-6 w-6 animate-spin text-ink-muted" />
          ) : (
            <Upload className="h-6 w-6 text-ink-muted" />
          )}
          <span className="text-sm font-medium">
            {uploading ? t('imports.wizard.upload.reading') : t('imports.wizard.upload.choose')}
          </span>
          <span className="text-xs text-ink-muted">{t('imports.wizard.upload.chooseHint')}</span>
        </button>
        {uploadError && (
          <p className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-sm text-red-700 dark:bg-red-950/50 dark:text-red-300">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {uploadError}
          </p>
        )}
      </div>
    );
  }

  function MappingAndResult() {
    if (!job || !spec) return null;
    return (
      <div className="space-y-5">
        {job.status === 'CANCELLED' && (
          <Banner tone="neutral" icon={XCircle} text={t('imports.wizard.status.cancelledHint')} />
        )}
        {job.status === 'COMMITTING' && (
          <Banner tone="info" icon={Loader2} spin text={t('imports.wizard.status.committingHint')} />
        )}
        {job.status === 'PARTIALLY_COMMITTED' && (
          <Banner
            tone="warning"
            icon={AlertTriangle}
            text={t('imports.wizard.status.partialHint', {
              done: job.totals?.committedRows ?? 0,
              total: job.totals?.valid ?? job.rowCount,
            })}
          />
        )}
        {job.status === 'FAILED' && (
          <Banner
            tone="danger"
            icon={XCircle}
            text={job.failureReason ?? t('imports.wizard.status.failedHint')}
          />
        )}

        <div className="rounded-lg bg-[var(--muted)] p-3 text-xs text-ink-muted">
          {t('imports.wizard.mapping.idempotencyNote', {
            fields: spec.matchedOn
              .map((key) => {
                const f = spec.fields.find((field) => field.key === key);
                return f ? localize(f.label, f.labelAr, lang) : key;
              })
              .join(` ${t('imports.wizard.mapping.then')} `),
          })}
          {dataset === 'OPENING_STOCK' && ` ${t('imports.wizard.mapping.openingStockNote')}`}
        </div>

        {(dataset === 'OPENING_STOCK' || dataset === 'INVENTORY_ITEMS') && (
          <div className="grid gap-3 sm:grid-cols-2">
            {dataset === 'OPENING_STOCK' && (
              <Field
                label={t('imports.wizard.mapping.warehouseCode')}
                hint={t('imports.wizard.mapping.warehouseCodeHint')}
              >
                <Input
                  list="imports-warehouses"
                  value={options.warehouseCode ?? ''}
                  disabled={mappingLocked}
                  onChange={(e) => {
                    setOptions((o) => ({ ...o, warehouseCode: e.target.value || undefined }));
                    setRun(null);
                  }}
                />
                <datalist id="imports-warehouses">
                  {warehouseRows.map((w) => (
                    <option key={w.id} value={w.code}>
                      {w.name}
                    </option>
                  ))}
                </datalist>
              </Field>
            )}
            {dataset === 'INVENTORY_ITEMS' && (
              <Field
                label={t('imports.wizard.mapping.defaultUnitCode')}
                hint={t('imports.wizard.mapping.defaultUnitCodeHint')}
              >
                <Input
                  list="imports-units"
                  value={options.defaultUnitCode ?? ''}
                  disabled={mappingLocked}
                  onChange={(e) => {
                    setOptions((o) => ({ ...o, defaultUnitCode: e.target.value || undefined }));
                    setRun(null);
                  }}
                />
                <datalist id="imports-units">
                  {unitRows.map((u) => (
                    <option key={u.id} value={u.code}>
                      {u.name}
                    </option>
                  ))}
                </datalist>
              </Field>
            )}
          </div>
        )}

        <div className="rounded-lg border border-[var(--border)]">
          <Table>
            <THead>
              <TR>
                <TH>{t('imports.wizard.mapping.column')}</TH>
                <TH>{t('imports.wizard.mapping.sample')}</TH>
                <TH>{t('imports.wizard.mapping.field')}</TH>
              </TR>
            </THead>
            <tbody>
              {columns.map((column) => {
                const sampleValue = sample.find((row) => row.cells[column])?.cells[column] ?? '—';
                return (
                  <TR key={column}>
                    <TD className="font-medium">{column}</TD>
                    <TD className="max-w-[220px] truncate text-ink-muted" title={sampleValue}>
                      {sampleValue}
                    </TD>
                    <TD>
                      <Select
                        // Named for a screen reader (and for a test): the
                        // column header is the only thing that distinguishes
                        // one row's dropdown from another's, and nothing else
                        // here associates the two.
                        aria-label={column}
                        value={mapping[column] ?? ''}
                        disabled={mappingLocked}
                        onChange={(e) => setFieldForColumn(column, e.target.value || null)}
                        className="h-9"
                      >
                        <option value="">{t('imports.wizard.mapping.notImported')}</option>
                        {spec.fields.map((f) => (
                          <option key={f.key} value={f.key}>
                            {localize(f.label, f.labelAr, lang)}
                            {f.required ? ' *' : ''}
                          </option>
                        ))}
                      </Select>
                    </TD>
                  </TR>
                );
              })}
            </tbody>
          </Table>
        </div>

        {unmappedColumns.length > 0 && (
          <p className="flex items-start gap-2 rounded-lg bg-[var(--muted)] p-3 text-xs">
            <FileWarning className="mt-0.5 h-4 w-4 shrink-0 text-[var(--warning,#b45309)]" />
            <span>
              <strong>{t('imports.wizard.mapping.unmappedTitle', { count: unmappedColumns.length })}</strong>{' '}
              {unmappedColumns.join(' · ')}
            </span>
          </p>
        )}

        {mappingError && !mappingLocked && (
          <p className="flex items-start gap-2 rounded-lg bg-red-50 p-3 text-xs text-red-700 dark:bg-red-950/50 dark:text-red-300">
            <XCircle className="mt-0.5 h-4 w-4 shrink-0" />
            {mappingError}
          </p>
        )}

        {run && RunSummary(run)}
      </div>
    );
  }

  function RunSummary(result: RunResult) {
    const nothingChanged = result.totals.created === 0 && result.totals.updated === 0;
    return (
      <div className="space-y-3 border-t pt-4">
        <div className="grid gap-3 sm:grid-cols-3 lg:grid-cols-5">
          {[
            { label: t('imports.wizard.result.rows'), value: result.totals.rows },
            { label: t('imports.wizard.result.create'), value: result.totals.created },
            { label: t('imports.wizard.result.update'), value: result.totals.updated },
            { label: t('imports.wizard.result.unchanged'), value: result.totals.unchanged },
            { label: t('imports.wizard.result.invalid'), value: result.totals.invalid },
          ].map((tile) => (
            <div key={tile.label} className="rounded-lg border border-[var(--border)] p-3">
              <p className="text-xs text-ink-muted">{tile.label}</p>
              <p className="mt-1 text-xl font-semibold tabular-nums">{tile.value}</p>
            </div>
          ))}
        </div>

        {result.status === 'INVALID' && (
          <Banner
            tone="danger"
            icon={XCircle}
            text={t('imports.wizard.result.invalidHint', { count: result.errorCount })}
          />
        )}
        {result.status === 'VALIDATED' && nothingChanged && result.totals.unchanged > 0 && (
          <Banner tone="info" icon={CheckCircle2} text={t('imports.wizard.result.nothingChangedHint')} />
        )}
        {result.status === 'VALIDATED' && !nothingChanged && (
          <Banner
            tone="success"
            icon={CheckCircle2}
            text={t('imports.wizard.result.readyHint', {
              create: result.totals.created,
              update: result.totals.updated,
            })}
          />
        )}
        {result.status === 'COMMITTED' && (
          <Banner
            tone="success"
            icon={CheckCircle2}
            text={t('imports.wizard.result.committedHint', {
              create: result.totals.created,
              update: result.totals.updated,
              unchanged: result.totals.unchanged,
            })}
          />
        )}
        {result.warningCount > 0 && (
          <p className="text-xs text-ink-muted">
            {t('imports.wizard.result.warningsHint', { count: result.warningCount })}
          </p>
        )}

        {result.issues.length > 0 && (
          <div className="max-h-[32vh] overflow-y-auto rounded-lg border border-[var(--border)]">
            <Table>
              <THead>
                <TR>
                  <TH>{t('imports.wizard.mapping.column')}</TH>
                  <TH>{t('imports.wizard.result.line')}</TH>
                  <TH>{t('imports.wizard.result.severity')}</TH>
                  <TH>{t('imports.wizard.result.problem')}</TH>
                </TR>
              </THead>
              <tbody>
                {result.issues.map((issue, index) => (
                  <TR key={`${issue.line}-${issue.field ?? issue.code}-${index}`}>
                    <TD className="text-ink-muted">{issue.column ?? '—'}</TD>
                    <TD className="tabular-nums">{issue.line}</TD>
                    <TD>
                      <Badge tone={issue.severity === 'ERROR' ? 'danger' : 'warning'}>
                        {issue.severity === 'ERROR'
                          ? t('imports.wizard.result.error')
                          : t('imports.wizard.result.warning')}
                      </Badge>
                    </TD>
                    <TD>{issue.message}</TD>
                  </TR>
                ))}
              </tbody>
            </Table>
          </div>
        )}
        {result.truncated && (
          <p className="text-xs text-ink-muted">{t('imports.wizard.result.truncatedHint')}</p>
        )}
      </div>
    );
  }
}

function Banner({
  tone,
  icon: Icon,
  text,
  spin,
}: {
  tone: 'neutral' | 'info' | 'warning' | 'danger' | 'success';
  icon: typeof AlertTriangle;
  text: string;
  spin?: boolean;
}) {
  const TONE_CLASS: Record<string, string> = {
    neutral: 'bg-[var(--muted)] text-ink-muted',
    info: 'bg-sky-50 text-sky-800 dark:bg-sky-950/50 dark:text-sky-200',
    warning: 'bg-amber-50 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200',
    danger: 'bg-red-50 text-red-700 dark:bg-red-950/50 dark:text-red-300',
    success: 'bg-green-50 text-green-800 dark:bg-green-950/50 dark:text-green-200',
  };
  return (
    <p className={`flex items-start gap-2 rounded-lg p-3 text-sm ${TONE_CLASS[tone]}`}>
      <Icon className={`mt-0.5 h-4 w-4 shrink-0 ${spin ? 'animate-spin' : ''}`} />
      <span>{text}</span>
    </p>
  );
}
